import React from 'react';
import Icon from '../../../components/AppIcon';

import PriceComparisonCheck from './PriceComparisonCheck';

const ComparisonSummary = ({ quotes, selectedCriteria, selectedVendor, onAddToComparison, projectNeeds = [], project = null }) => {
  const calculateSummaryStats = () => {
    if (!quotes || quotes?.length === 0) return null;
    const receivedQuotes = quotes?.filter(q => q?.status === 'received');
    if (receivedQuotes?.length === 0) return null;
    const prices = receivedQuotes?.map(q => q?.price);
    const lowestPrice = Math.min(...prices);
    const highestPrice = Math.max(...prices);
    const averagePrice = prices?.reduce((sum, price) => sum + price, 0) / prices?.length;
    const savings = highestPrice - lowestPrice;
    const bestValueQuote = receivedQuotes?.find(q => q?.price === lowestPrice);
    const highestRatedQuote = receivedQuotes?.reduce((prev, current) =>
      (prev?.vendor?.rating > current?.vendor?.rating) ? prev : current
    );
    return {
      totalQuotes: quotes?.length,
      receivedQuotes: receivedQuotes?.length,
      pendingQuotes: quotes?.filter(q => q?.status === 'pending')?.length,
      lowestPrice,
      highestPrice,
      averagePrice,
      savings,
      bestValueQuote,
      highestRatedQuote,
      savingsPercentage: ((savings / highestPrice) * 100)?.toFixed(1)
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(amount);
  };

  const getRecommendationInsights = () => {
    if (!quotes?.length) return [];
    const receivedQuotes = quotes?.filter(q => q?.status === 'received' || q?.status === 'submitted' || q?.status === 'accepted');
    if (!receivedQuotes?.length) return [];

    const prices = receivedQuotes?.map(q => q?.price)?.filter(p => p != null && p > 0);
    const minPrice = prices?.length ? Math.min(...prices) : null;
    const maxPrice = prices?.length ? Math.max(...prices) : null;

    const insights = [];

    if (minPrice != null && maxPrice != null) {
      insights?.push({
        title: 'Price Range',
        description: `$${minPrice?.toLocaleString()} – $${maxPrice?.toLocaleString()}`,
        icon: 'DollarSign',
        gradient: 'linear-gradient(135deg, #F58A54, #F06F6B)',
      });
    }

    // Highest rated vendor
    const ratedQuotes = receivedQuotes?.filter(q => q?.vendor?.rating > 0);
    if (ratedQuotes?.length > 0) {
      const topRated = ratedQuotes?.reduce((best, q) =>
        (q?.vendor?.rating || 0) > (best?.vendor?.rating || 0) ? q : best
      );
      insights?.push({
        title: 'Top Rated',
        description: `${topRated?.vendor?.name} — ${topRated?.vendor?.rating}★`,
        icon: 'Star',
        gradient: 'linear-gradient(135deg, #F06F6B, #E0528A)',
      });
    }

    // Best price vendor
    if (minPrice != null && prices?.length > 1) {
      const cheapest = receivedQuotes?.find(q => q?.price === minPrice);
      if (cheapest) {
        insights?.push({
          title: 'Best Price',
          description: `${cheapest?.vendor?.name} — $${minPrice?.toLocaleString()}`,
          icon: 'Tag',
          gradient: 'linear-gradient(135deg, #E0528A, #c026d3)',
        });
      }
    }

    // Fastest delivery
    const quotesWithDelivery = receivedQuotes?.filter(q => q?.deliveryTime);
    if (quotesWithDelivery?.length > 0) {
      const fastest = quotesWithDelivery?.reduce((best, q) => {
        const bestDays = best?.deliveryTime?.toLowerCase()?.includes('same') ? 0 : parseInt(best?.deliveryTime?.match(/(\d+)/)?.[1] || '999');
        const curDays = q?.deliveryTime?.toLowerCase()?.includes('same') ? 0 : parseInt(q?.deliveryTime?.match(/(\d+)/)?.[1] || '999');
        return curDays < bestDays ? q : best;
      });
      insights?.push({
        title: 'Fastest Delivery',
        description: `${fastest?.vendor?.name} — ${fastest?.deliveryTime}`,
        icon: 'Truck',
        gradient: 'linear-gradient(135deg, #10b981, #06b6d4)',
      });
    }

    return insights;
  };

  // Product-type subcategory IDs — purchases fulfilled on third-party retailer sites
  const PRODUCT_CATEGORY_IDS = new Set([
    'furniture', 'mattresses', 'major-appliances', 'home-gym', 'home-security',
    'computers-laptops', 'tablets', 'smartphones', 'tv-home-theater',
    'smart-home-devices', 'audio-equipment', 'headphones', 'wearables',
    'car-purchase', 'motorcycle', 'rv', 'boat', 'tires',
    'engagement-rings', 'baby-products', 'medical-equipment'
  ]);

  const isProductCategory = PRODUCT_CATEGORY_IDS?.has(project?.purchaseType);

  const handleProceedWithVendor = () => {
    if (!selectedVendor) return;
    if (isProductCategory) {
      const matchingQuote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
      const vendorUrl = matchingQuote?.vendor?.website || selectedVendor?.website;
      if (vendorUrl) {
        const url = vendorUrl?.startsWith('http') ? vendorUrl : `https://${vendorUrl}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const searchQuery = encodeURIComponent(selectedVendor?.name);
        window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
      }
    }
    // For service categories, the checkout flow is handled from ComparisonTable
  };

  const handleYoutubeReviews = () => {
    if (!selectedVendor) { alert('Please select a vendor first to view reviews'); return; }
    const quote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
    const productName = quote?.specifications?.[0] || 'Product';
    const searchQuery = encodeURIComponent(`${productName} ${selectedVendor?.name}`);
    window.open(`https://www.youtube.com/results?search_query=${searchQuery}`, '_blank');
  };

  const handleTikTokReviews = () => {
    if (!selectedVendor) { alert('Please select a vendor first to view reviews'); return; }
    const quote = quotes?.find(q => q?.vendor?.name === selectedVendor?.name);
    const productName = quote?.specifications?.[0] || 'Product';
    const searchQuery = encodeURIComponent(`${productName} ${selectedVendor?.name}`);
    window.open(`https://www.tiktok.com/search?q=${searchQuery}`, '_blank');
  };

  const stats = calculateSummaryStats();
  const insights = getRecommendationInsights();

  const StatCard = ({ value, label, gradient }) => (
    <div
      className="rounded-xl p-4 flex flex-col items-center text-center"
      style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', border: '1px solid #fde8d8' }}
    >
      <div className="text-2xl font-bold" style={{ background: gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        {value}
      </div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Selection Card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1.5px solid #fde8d8', boxShadow: '0 2px 12px rgba(245,138,84,0.07)' }}
      >
        {/* Card header */}
        <div
          className="px-5 py-4"
          style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}
        >
          <div className="flex items-center gap-2">
            <Icon name="ShoppingBag" size={18} color="white" />
            <h3 className="text-base font-bold text-white">Your Selection</h3>
          </div>
        </div>

        <div className="bg-white px-5 py-5">
          {selectedVendor ? (
            <div className="space-y-4">
              {/* Selected vendor highlight */}
              <div
                className="rounded-xl p-4"
                style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', border: '1.5px solid #fde8d8' }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                  >
                    <Icon name="CheckCircle2" size={18} color="white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{selectedVendor?.name}</h4>
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={12} className="text-amber-400" />
                      <span className="text-xs text-gray-500">{selectedVendor?.rating} rating</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">Selected as your preferred vendor</p>
              </div>

              {/* Review buttons */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleYoutubeReviews}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-colors hover:opacity-80"
                  style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                >
                  <Icon name="Youtube" size={13} />
                  YouTube
                </button>
                <button
                  onClick={handleTikTokReviews}
                  className="flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-colors hover:opacity-80"
                  style={{ background: '#18181b', color: 'white' }}
                >
                  <Icon name="Video" size={13} />
                  TikTok
                </button>
              </div>

            </div>
          ) : (
            <div className="text-center py-6">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
                style={{ background: 'linear-gradient(135deg, #fff5f0, #fde8d8)' }}
              >
                <Icon name="ShoppingBag" size={24} style={{ color: '#F58A54' }} />
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">No vendor selected yet</p>
              <p className="text-xs text-gray-400">Pick a vendor from the cards to get started</p>
            </div>
          )}
        </div>
      </div>
      {/* Price check */}
      {selectedVendor && (
        <PriceComparisonCheck 
          selectedVendor={selectedVendor} 
          quotes={quotes} 
          onAddToComparison={onAddToComparison}
          project={null}
          projectNeeds={projectNeeds}
        />
      )}
      {/* Quick Stats */}
      {stats && (
        <div
          className="rounded-2xl overflow-hidden"
          style={{ border: '1.5px solid #fde8d8' }}
        >
          <div className="px-5 py-3" style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', borderBottom: '1px solid #fde8d8' }}>
            <div className="flex items-center gap-2">
              <Icon name="BarChart3" size={15} style={{ color: '#F58A54' }} />
              <h3 className="text-sm font-bold text-gray-800">At a Glance</h3>
            </div>
          </div>
          <div className="bg-white p-4 grid grid-cols-1 gap-3">
            <StatCard
              value={stats?.totalQuotes}
              label="Options to Compare"
              gradient="linear-gradient(135deg, #F58A54, #F06F6B)"
            />
            <StatCard
              value={`$${stats?.lowestPrice?.toLocaleString()}`}
              label="Best Price Found"
              gradient="linear-gradient(135deg, #F06F6B, #E0528A)"
            />
            <StatCard
              value={`${stats?.highestRatedQuote?.vendor?.rating}★`}
              label="Top Rated Vendor"
              gradient="linear-gradient(135deg, #E0528A, #c026d3)"
            />
            {stats?.savings > 0 && (
              <StatCard
                value={`$${stats?.savings?.toLocaleString()}`}
                label={`Save vs. highest (${stats?.savingsPercentage}%)`}
                gradient="linear-gradient(135deg, #10b981, #06b6d4)"
              />
            )}
          </div>
        </div>
      )}
      {/* Key Insights */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1.5px solid #fde8d8' }}
      >
        <div className="px-5 py-3" style={{ background: 'linear-gradient(135deg, #fff5f0, #fff0f5)', borderBottom: '1px solid #fde8d8' }}>
          <div className="flex items-center gap-2">
            <Icon name="Lightbulb" size={15} style={{ color: '#F58A54' }} />
            <h3 className="text-sm font-bold text-gray-800">Key Insights</h3>
          </div>
        </div>
        <div className="bg-white p-4 space-y-3">
          {insights?.map((insight, index) => (
            <div key={index} className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: insight?.gradient }}
              >
                <Icon name={insight?.icon} size={14} color="white" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">{insight?.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{insight?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Export / Share */}
      <div className="grid grid-cols-2 gap-2">
        <button
          className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold border transition-colors hover:bg-orange-50"
          style={{ borderColor: '#fde8d8', color: '#F58A54' }}
        >
          <Icon name="Download" size={13} />
          Export
        </button>
        <button
          className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold border transition-colors hover:bg-orange-50"
          style={{ borderColor: '#fde8d8', color: '#F58A54' }}
        >
          <Icon name="Share" size={13} />
          Share
        </button>
      </div>
    </div>
  );
};

export default ComparisonSummary;